import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend
} from "recharts";
import {
  Plus,
  Minus,
  Calendar,
  DollarSign,
  TrendingUp,
  TrendingDown,
  RotateCcw,
  Sparkles,
  ArrowRight,
  AlertTriangle,
  CheckCircle2,
  X
} from "lucide-react";

export const WhatIfSimulator = ({ originalForecast, bills }) => {
  const { user } = useAuth();
  const [scenarios, setScenarios] = useState([]);
  const [simulatedForecast, setSimulatedForecast] = useState(null);
  const [activeTab, setActiveTab] = useState("delay");
  
  // Form states
  const [selectedBill, setSelectedBill] = useState("");
  const [delayDays, setDelayDays] = useState(14);
  const [oneTimeAmount, setOneTimeAmount] = useState(500);
  const [oneTimeDate, setOneTimeDate] = useState("");
  const [oneTimeType, setOneTimeType] = useState("income");

  // Set default date to 7 days from now
  useEffect(() => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    setOneTimeDate(date.toISOString().split("T")[0]);
  }, []);

  const formatCurrency = (value) => {
    const currency = user?.currency || "CAD";
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
    } catch {
      return dateStr;
    }
  };

  // Apply scenarios to forecast
  const applyScenarios = () => {
    if (!originalForecast || scenarios.length === 0) {
      setSimulatedForecast(null);
      return;
    }

    // Deep clone the original forecast
    const simulated = JSON.parse(JSON.stringify(originalForecast.daily_forecast));
    
    // Apply each scenario
    scenarios.forEach(scenario => {
      if (scenario.type === "delay_bill") {
        // Find and modify bill occurrences
        simulated.forEach(day => {
          const billIndex = day.bills.findIndex(b => b.id === scenario.billId);
          if (billIndex !== -1) {
            const bill = day.bills[billIndex];
            // Remove from original date
            day.expenses -= bill.amount;
            day.bills.splice(billIndex, 1);
            
            // Add to delayed date
            const originalDate = new Date(day.date);
            const newDate = new Date(originalDate);
            newDate.setDate(newDate.getDate() + scenario.delayDays);
            const newDateStr = newDate.toISOString().split("T")[0];
            
            const targetDay = simulated.find(d => d.date === newDateStr);
            if (targetDay) {
              targetDay.bills.push({ ...bill, delayed: true });
              targetDay.expenses += bill.amount;
            }
          }
        });
      } else if (scenario.type === "one_time") {
        // Add one-time income or expense
        const targetDay = simulated.find(d => d.date === scenario.date);
        if (targetDay) {
          if (scenario.isIncome) {
            targetDay.income += scenario.amount;
          } else {
            targetDay.expenses += scenario.amount;
            targetDay.bills.push({
              id: "onetime-" + Date.now(),
              merchant: scenario.description,
              amount: scenario.amount,
              simulated: true
            });
          }
        }
      } else if (scenario.type === "skip_bill") {
        // Remove bill from forecast
        simulated.forEach(day => {
          const billIndex = day.bills.findIndex(b => b.id === scenario.billId);
          if (billIndex !== -1) {
            const bill = day.bills[billIndex];
            day.expenses -= bill.amount;
            day.bills.splice(billIndex, 1);
          }
        });
      }
    });

    // Recalculate running balances
    let runningBalance = originalForecast.checking_balance;
    simulated.forEach(day => {
      day.starting_balance = runningBalance;
      day.ending_balance = runningBalance + day.income - day.expenses;
      day.is_negative = day.ending_balance < 0;
      runningBalance = day.ending_balance;
    });

    setSimulatedForecast(simulated);
  };

  useEffect(() => {
    applyScenarios();
  }, [scenarios, originalForecast]);

  const addDelayScenario = () => {
    if (!selectedBill) return;
    const bill = bills.find(b => b.id === selectedBill);
    if (!bill) return;

    setScenarios(prev => [...prev, {
      id: Date.now(),
      type: "delay_bill",
      billId: selectedBill,
      billName: bill.merchant_display || bill.merchant,
      billAmount: bill.avg_amount,
      delayDays: delayDays,
      description: `Delay ${bill.merchant_display || bill.merchant} by ${delayDays} days`
    }]);
    setSelectedBill("");
  };

  const addOneTimeScenario = () => {
    if (!oneTimeAmount || !oneTimeDate) return;

    setScenarios(prev => [...prev, {
      id: Date.now(),
      type: "one_time",
      amount: oneTimeAmount,
      date: oneTimeDate,
      isIncome: oneTimeType === "income",
      description: oneTimeType === "income" 
        ? `Add ${formatCurrency(oneTimeAmount)} income on ${formatDate(oneTimeDate)}`
        : `Add ${formatCurrency(oneTimeAmount)} expense on ${formatDate(oneTimeDate)}`
    }]);
  };

  const addSkipScenario = () => {
    if (!selectedBill) return;
    const bill = bills.find(b => b.id === selectedBill);
    if (!bill) return;

    setScenarios(prev => [...prev, {
      id: Date.now(),
      type: "skip_bill",
      billId: selectedBill,
      billName: bill.merchant_display || bill.merchant,
      billAmount: bill.avg_amount,
      description: `Skip ${bill.merchant_display || bill.merchant} (${formatCurrency(bill.avg_amount)}/mo)`
    }]);
    setSelectedBill("");
  };

  const removeScenario = (id) => {
    setScenarios(prev => prev.filter(s => s.id !== id));
  };

  const clearAllScenarios = () => {
    setScenarios([]);
  };

  // Prepare comparison chart data
  const getComparisonData = () => {
    if (!originalForecast || !simulatedForecast) return [];
    
    return originalForecast.daily_forecast
      .filter((_, i) => i % 3 === 0) // Sample every 3 days
      .map((day, i) => ({
        date: day.date,
        original: day.ending_balance,
        simulated: simulatedForecast[i * 3]?.ending_balance || day.ending_balance
      }));
  };

  // Calculate impact summary
  const getImpactSummary = () => {
    if (!originalForecast || !simulatedForecast) return null;

    const originalShortfalls = originalForecast.daily_forecast.filter(d => d.is_negative).length;
    const simulatedShortfalls = simulatedForecast.filter(d => d.is_negative).length;
    const originalEndBalance = originalForecast.daily_forecast[originalForecast.daily_forecast.length - 1]?.ending_balance || 0;
    const simulatedEndBalance = simulatedForecast[simulatedForecast.length - 1]?.ending_balance || 0;
    const balanceDiff = simulatedEndBalance - originalEndBalance;

    return {
      originalShortfalls,
      simulatedShortfalls,
      shortfallDiff: simulatedShortfalls - originalShortfalls,
      originalEndBalance,
      simulatedEndBalance,
      balanceDiff,
      improved: simulatedShortfalls < originalShortfalls || (simulatedShortfalls === originalShortfalls && balanceDiff > 0)
    };
  };

  const comparisonData = getComparisonData();
  const impact = getImpactSummary();
  const enabledBills = bills?.filter(b => b.enabled) || [];

  return (
    <Card className="border-border/50" data-testid="whatif-simulator">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-amber-500" />
          What If Simulator
        </CardTitle>
        <CardDescription>
          Test scenarios to see how changes affect your cash flow
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Scenario Builder */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="delay" className="text-xs">
              <Calendar className="h-3 w-3 mr-1" />
              Delay Bill
            </TabsTrigger>
            <TabsTrigger value="onetime" className="text-xs">
              <DollarSign className="h-3 w-3 mr-1" />
              One-Time
            </TabsTrigger>
            <TabsTrigger value="skip" className="text-xs">
              <X className="h-3 w-3 mr-1" />
              Skip Bill
            </TabsTrigger>
          </TabsList>

          {/* Delay Bill Tab */}
          <TabsContent value="delay" className="space-y-4 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Label className="text-sm">Select Bill</Label>
                <Select value={selectedBill} onValueChange={setSelectedBill}>
                  <SelectTrigger data-testid="delay-bill-select">
                    <SelectValue placeholder="Choose a bill to delay" />
                  </SelectTrigger>
                  <SelectContent>
                    {enabledBills.map(bill => (
                      <SelectItem key={bill.id} value={bill.id}>
                        {bill.merchant_display || bill.merchant} - {formatCurrency(bill.avg_amount)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Delay by (days)</Label>
                <Input
                  type="number"
                  value={delayDays}
                  onChange={(e) => setDelayDays(parseInt(e.target.value) || 0)}
                  min={1}
                  max={60}
                  data-testid="delay-days-input"
                />
              </div>
            </div>
            <Button 
              onClick={addDelayScenario} 
              disabled={!selectedBill}
              className="gap-2"
              data-testid="add-delay-scenario-btn"
            >
              <Plus className="h-4 w-4" />
              Add Scenario
            </Button>
          </TabsContent>

          {/* One-Time Income/Expense Tab */}
          <TabsContent value="onetime" className="space-y-4 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm">Type</Label>
                <Select value={oneTimeType} onValueChange={setOneTimeType}>
                  <SelectTrigger data-testid="onetime-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">
                      <span className="flex items-center gap-2">
                        <Plus className="h-3 w-3 text-emerald-600" />
                        Income
                      </span>
                    </SelectItem>
                    <SelectItem value="expense">
                      <span className="flex items-center gap-2">
                        <Minus className="h-3 w-3 text-rose-600" />
                        Expense
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Amount</Label>
                <Input
                  type="number"
                  value={oneTimeAmount}
                  onChange={(e) => setOneTimeAmount(parseFloat(e.target.value) || 0)}
                  min={1}
                  data-testid="onetime-amount-input"
                />
              </div>
              <div>
                <Label className="text-sm">Date</Label>
                <Input
                  type="date"
                  value={oneTimeDate}
                  onChange={(e) => setOneTimeDate(e.target.value)}
                  data-testid="onetime-date-input"
                />
              </div>
            </div>
            <Button 
              onClick={addOneTimeScenario}
              disabled={!oneTimeAmount || !oneTimeDate}
              className="gap-2"
              data-testid="add-onetime-scenario-btn"
            >
              <Plus className="h-4 w-4" />
              Add {oneTimeType === "income" ? "Income" : "Expense"}
            </Button>
          </TabsContent>

          {/* Skip Bill Tab */}
          <TabsContent value="skip" className="space-y-4 pt-4">
            <div>
              <Label className="text-sm">Select Bill to Skip</Label>
              <Select value={selectedBill} onValueChange={setSelectedBill}>
                <SelectTrigger data-testid="skip-bill-select">
                  <SelectValue placeholder="Choose a bill to skip entirely" />
                </SelectTrigger>
                <SelectContent>
                  {enabledBills.map(bill => (
                    <SelectItem key={bill.id} value={bill.id}>
                      {bill.merchant_display || bill.merchant} - {formatCurrency(bill.avg_amount)}/mo
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                This removes all occurrences of the bill from the forecast
              </p>
            </div>
            <Button 
              onClick={addSkipScenario}
              disabled={!selectedBill}
              variant="destructive"
              className="gap-2"
              data-testid="add-skip-scenario-btn"
            >
              <X className="h-4 w-4" />
              Skip This Bill
            </Button>
          </TabsContent>
        </Tabs>

        {/* Active Scenarios */}
        {scenarios.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">Active Scenarios ({scenarios.length})</h4>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearAllScenarios}
                className="text-muted-foreground gap-1"
                data-testid="clear-scenarios-btn"
              >
                <RotateCcw className="h-3 w-3" />
                Clear All
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {scenarios.map(scenario => (
                <Badge 
                  key={scenario.id}
                  variant="secondary"
                  className="pl-3 pr-1 py-1.5 gap-2"
                >
                  <span className="text-xs">{scenario.description}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 hover:bg-destructive/20"
                    onClick={() => removeScenario(scenario.id)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Impact Summary */}
        {impact && scenarios.length > 0 && (
          <div className={`p-4 rounded-lg border ${impact.improved ? "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800" : "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800"}`}>
            <div className="flex items-start gap-3">
              {impact.improved ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <h4 className={`font-medium ${impact.improved ? "text-emerald-900 dark:text-emerald-100" : "text-amber-900 dark:text-amber-100"}`}>
                  {impact.improved ? "This improves your cash flow!" : "Consider the trade-offs"}
                </h4>
                <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">End Balance</p>
                    <div className="flex items-center gap-2">
                      <span className="font-mono">{formatCurrency(impact.originalEndBalance)}</span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      <span className={`font-mono font-medium ${impact.balanceDiff >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                        {formatCurrency(impact.simulatedEndBalance)}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Shortfall Days</p>
                    <div className="flex items-center gap-2">
                      <span className="font-mono">{impact.originalShortfalls}</span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      <span className={`font-mono font-medium ${impact.shortfallDiff <= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                        {impact.simulatedShortfalls}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Comparison Chart */}
        {scenarios.length > 0 && comparisonData.length > 0 && (
          <div>
            <h4 className="text-sm font-medium mb-3">Forecast Comparison</h4>
            <div className="h-[200px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={comparisonData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="originalGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#94a3b8" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#94a3b8" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="simulatedGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={formatDate}
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tickFormatter={(value) => "$" + (value / 1000).toFixed(0) + "k"}
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip 
                    formatter={(value, name) => [formatCurrency(value), name === "original" ? "Original" : "With Changes"]}
                    labelFormatter={formatDate}
                  />
                  <Legend />
                  <ReferenceLine y={0} stroke="#ef4444" strokeDasharray="5 5" strokeWidth={1} />
                  <Area
                    type="monotone"
                    dataKey="original"
                    name="Original"
                    stroke="#94a3b8"
                    strokeWidth={2}
                    fill="url(#originalGradient)"
                    strokeDasharray="5 5"
                  />
                  <Area
                    type="monotone"
                    dataKey="simulated"
                    name="With Changes"
                    stroke="#10b981"
                    strokeWidth={2}
                    fill="url(#simulatedGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Empty State */}
        {scenarios.length === 0 && (
          <div className="text-center py-6 text-muted-foreground">
            <Sparkles className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Add scenarios above to see how changes affect your forecast</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
